interface DiscordMessage {
  id: string;
  content: string;
  timestamp: string;
  author: {
    id: string;
    username: string;
    global_name?: string;
    avatar?: string;
  };
  reactions?: Array<{
    emoji: {
      name: string;
    };
    count: number;
  }>;
}

interface Testimonial {
  id: string;
  feature: string;
  seller: string;
  rating: number;
  feedback: string;
  author: {
    username: string;
    displayName: string;
    avatar?: string;
  };
  timestamp: string;
  reactions: number;
}

const DISCORD_API_BASE = 'https://discord.com/api/v9';
const AUTH_TOKEN = 'NzI1ODA1Mjk0NzU5ODM3NzM2.GBgzF0.ZU5erN10xFNej5sF_f-i_PQzF_GUPi3wiN4BoE';

export const fetchTestimonials = async (): Promise<Testimonial[]> => {
  try {
    const response = await fetch(`${DISCORD_API_BASE}/channels/1282384673993003018/messages?limit=50`, {
      headers: {
        'Authorization': AUTH_TOKEN,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Discord API error: ${response.status}`);
    }

    const messages: DiscordMessage[] = await response.json();

    // Filter and parse testimonials
    const testimonials: Testimonial[] = messages
      .filter(msg => msg.content && !msg.content.includes('__**Stickied Message:**__'))
      .map(msg => parseTestimonialMessage(msg))
      .filter(testimonial => testimonial !== null) as Testimonial[];

    return testimonials;
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    return [];
  }
};

const parseTestimonialMessage = (message: DiscordMessage): Testimonial | null => {
  try {
    const content = message.content;

    // Extract information using regex
    const featureMatch = content.match(/Feature\s*:\s*(.+?)(?:\n|$)/i);
    const sellerMatch = content.match(/Seller\s*:\s*(.+?)(?:\n|$)/i);
    const ratingMatch = content.match(/Rating\s*:\s*(.+?)(?:\n|$)/i);
    const feedbackMatch = content.match(/FeedBack\s*:\s*(.+?)(?:\n|$)/i);

    if (!featureMatch || !sellerMatch || !ratingMatch || !feedbackMatch) {
      return null;
    }

    // Parse rating (handle both star emojis and numeric formats)
    const ratingText = ratingMatch[1].trim();
    let starCount = 0;

    if (ratingText.includes('⭐')) {
      // Count star emojis
      starCount = (ratingText.match(/⭐/g) || []).length;
    } else if (ratingText.includes('/')) {
      // Handle fraction format like "100/10" or "5/5"
      const parts = ratingText.split('/');
      if (parts.length === 2) {
        const numerator = parseFloat(parts[0]);
        const denominator = parseFloat(parts[1]);
        if (!isNaN(numerator) && !isNaN(denominator) && denominator > 0) {
          // Convert to 5-star scale
          starCount = Math.round((numerator / denominator) * 5);
        }
      }
    } else {
      // Handle direct numbers
      const numRating = parseFloat(ratingText);
      if (!isNaN(numRating)) {
        if (numRating > 5) {
          // Assume it's out of 10 or 100, convert to 5-star
          starCount = Math.round((numRating / Math.max(numRating > 10 ? 100 : 10, numRating)) * 5);
        } else {
          starCount = Math.round(numRating);
        }
      }
    }

    // Ensure rating is between 1-5
    starCount = Math.max(1, Math.min(5, starCount));

    // Get reaction count
    const thumbsUpReaction = message.reactions?.find(r => r.emoji.name === '👍');
    const reactionCount = thumbsUpReaction?.count || 0;

    return {
      id: message.id,
      feature: featureMatch[1].trim(),
      seller: sellerMatch[1].trim(),
      rating: starCount,
      feedback: feedbackMatch[1].trim(),
      author: {
        username: message.author.username,
        displayName: message.author.global_name || message.author.username,
        avatar: message.author.avatar
      },
      timestamp: message.timestamp,
      reactions: reactionCount
    };
  } catch (error) {
    console.error('Error parsing testimonial message:', error);
    return null;
  }
};

export type { Testimonial };